import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import * as api from '@/services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCode,
  faFileCode,
  faFloppyDisk,
  faPlay,
  faPlus,
  faRotate,
  faShieldHalved,
  faSpinner,
  faToggleOff,
  faToggleOn,
  faTrash,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

interface IndicatorMeta {
  id: number;
  name: string;
  description: string;
  type: 'lag' | 'lead';
  needsDevInfo: boolean;
  scope: 'per-user' | 'overall';
  source: 'jira' | 'git' | 'both' | 'linear' | 'monday' | 'trello';
  enabled: boolean;
  isCustom: boolean;
  isCustomized: boolean;
}

interface IndicatorDetail {
  id: number;
  name: string;
  description: string;
  type: string;
  needsDevInfo: boolean;
  scope: 'per-user' | 'overall';
  source: 'jira' | 'git' | 'both' | 'linear' | 'monday' | 'trello';
  defaultCode: string;
  effectiveCode: string;
  isCustomized: boolean;
  enabled: boolean;
  isCustom: boolean;
}

interface IndicatorConfigEditorProps {
  onClose: () => void;
  inline?: boolean;
}

export function IndicatorConfigEditor({ onClose, inline = false }: IndicatorConfigEditorProps) {
  const [indicators, setIndicators] = useState<IndicatorMeta[]>([]);
  const [customMap, setCustomMap] = useState<Record<number, string>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<IndicatorDetail | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; error?: string; securityWarnings?: string[] } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [showLag, setShowLag] = useState(true);
  const [showLead, setShowLead] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newIndicator, setNewIndicator] = useState({ name: '', description: '', type: 'lag' as 'lag' | 'lead', needsDevInfo: false, scope: 'per-user' as 'per-user' | 'overall', source: 'jira' as 'jira' | 'git' | 'both' | 'linear' | 'monday' | 'trello' });
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Track dark mode changes for Monaco theme switching
  useEffect(() => {
    if (!inline) return;
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [inline]);


  // Load indicator list
  useEffect(() => {
    loadIndicators();
  }, []);

  const loadIndicators = async () => {
    setLoading(true);
    try {
      const data = await api.getIndicators();
      setIndicators((data.indicators || []) as any);
      setCustomMap((data.custom || {}) as any);
    } catch (err) {
      console.error('Failed to load indicators:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIndicatorDetail = useCallback(async (id: number) => {
    try {
      const data = await api.getIndicatorDetail(id) as any;
      setDetail(data);
      setCode(data?.effectiveCode || data?.defaultCode || data?.code || '');
      setHasUnsavedChanges(false);
      setValidationResult(null);
      setSaveResult(null);
    } catch (err) {
      console.error('Failed to load indicator detail:', err);
    }
  }, []);

  const selectIndicator = useCallback((id: number) => {
    if (hasUnsavedChanges && selectedId !== null) {
      if (!confirm('You have unsaved changes. Discard them?')) return;
    }
    setSelectedId(id);
    loadIndicatorDetail(id);
  }, [hasUnsavedChanges, selectedId, loadIndicatorDetail]);

  const validateCode = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await api.validateIndicatorCode(code);
      setValidationResult(result);
    } catch (err: any) {
      setValidationResult({ valid: false, error: err.message });
    } finally {
      setValidating(false);
    }
  };

  const saveCode = async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const result = await api.updateIndicatorCode(selectedId, code);
      if (result.success) {
        setSaveResult({ success: true });
        setHasUnsavedChanges(false);
        setCustomMap((prev) => ({ ...prev, [selectedId]: code }));
        if (detail) setDetail({ ...detail, isCustomized: true, effectiveCode: code });
        setTimeout(() => setSaveResult(null), 3000);
      } else {
        setSaveResult({ success: false, error: 'Failed to save' });
      }
    } catch (err: any) {
      setSaveResult({ success: false, error: err.message });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!selectedId || !detail) return;
    if (!confirm(`Reset "${detail.name}" to default code? Your custom code will be deleted.`)) return;

    try {
      await api.resetIndicatorCode(selectedId);
      setCustomMap((prev) => {
        const next = { ...prev };
        delete next[selectedId];
        return next;
      });
      setCode(detail.defaultCode);
      setDetail({ ...detail, isCustomized: false, effectiveCode: detail.defaultCode });
      setHasUnsavedChanges(false);
      setSaveResult({ success: true });
      setTimeout(() => setSaveResult(null), 3000);
    } catch (err: any) {
      setSaveResult({ success: false, error: err.message });
    }
  };

  const resetAll = async () => {
    if (!confirm('Reset ALL indicators to their default code? This cannot be undone.')) return;
    try {
      await api.resetAllIndicatorCode();
      setCustomMap({});
      if (detail && selectedId) {
        setCode(detail.defaultCode);
        setDetail({ ...detail, isCustomized: false, effectiveCode: detail.defaultCode });
        setHasUnsavedChanges(false);
      }
      await loadIndicators();
    } catch (err) {
      console.error('Failed to reset all:', err);
    }
  };

  const toggleIndicator = async (id: number, enabled: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.toggleIndicator(id, enabled);
      setIndicators((prev) =>
        prev.map((ind) => ind.id === id ? { ...ind, enabled } : ind)
      );
      if (detail && detail.id === id) {
        setDetail({ ...detail, enabled });
      }
    } catch (err) {
      console.error('Failed to toggle indicator:', err);
    }
  };

  const createNewIndicator = async () => {
    if (!newIndicator.name.trim()) return;
    try {
      const data = await api.createIndicator(newIndicator as any);
      if (data.indicator) {
        setShowCreateModal(false);
        setNewIndicator({ name: '', description: '', type: 'lag', needsDevInfo: false, scope: 'per-user', source: 'jira' as 'jira' | 'git' | 'both' | 'linear' | 'monday' | 'trello' });
        await loadIndicators();
        if (data.indicator?.id) {
          selectIndicator(data.indicator.id);
        }
      }
    } catch (err) {
      console.error('Failed to create indicator:', err);
    }
  };

  const deleteCustomIndicator = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const ind = indicators.find((i) => i.id === id);
    if (!confirm(`Delete "${ind?.name || 'this indicator'}"? This cannot be undone.`)) return;
    try {
      const data = await api.deleteIndicator(id);
      if (data.deleted) {
        if (selectedId === id) {
          setSelectedId(null);
          setDetail(null);
        }
        await loadIndicators();
      }
    } catch (err) {
      console.error('Failed to delete indicator:', err);
    }
  };

  const lagIndicators = indicators.filter((i) => i.type === 'lag');
  const leadIndicators = indicators.filter((i) => i.type === 'lead');
  const customCount = Object.keys(customMap).length;
  const disabledCount = indicators.filter((i) => !i.enabled).length;

  // Keyboard shortcut: Ctrl/Cmd+S to save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (selectedId && hasUnsavedChanges) saveCode();
      }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, hasUnsavedChanges, code]);

  const editorContent = (
      <div className={inline ? 'bg-white dark:bg-slate-900 w-full h-[calc(100vh-4rem)] flex flex-col overflow-hidden' : 'bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden'}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${inline ? 'border-gray-200 dark:border-slate-700/50' : 'border-slate-700/50'}`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faFileCode} className="h-5 w-5 text-blue-400" />
            <div>
              <h2 className={`text-lg font-semibold ${inline ? 'text-gray-900 dark:text-white' : 'text-white'}`}>Indicator Code Editor</h2>
              <p className={`text-xs ${inline ? 'text-gray-500 dark:text-slate-400' : 'text-slate-400'}`}>
                Edit the JavaScript code that calculates each health indicator.
                {customCount > 0 && (
                  <span className="ml-2 text-amber-400">{customCount} customized</span>
                )}
                {disabledCount > 0 && (
                  <span className={`ml-2 ${inline ? 'text-gray-400 dark:text-slate-500' : 'text-slate-500'}`}>{disabledCount} disabled</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {customCount > 0 && (
              <button
                onClick={resetAll}
                className="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Reset All to Defaults
              </button>
            )}
            {!inline && (
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors">
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
            )}
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar: indicator list */}
          <div className={`w-72 border-r overflow-y-auto flex-shrink-0 ${inline ? 'border-gray-200 bg-white/40 backdrop-blur-sm dark:border-slate-700/50 dark:bg-transparent' : 'border-slate-700/50'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 text-slate-400 animate-spin" />
              </div>
            ) : (
              <div className="py-2">
                {/* Lag indicators */}
                <button
                  onClick={() => setShowLag(!showLag)}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-wider ${inline ? 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800/50' : 'text-slate-400 hover:bg-slate-800/50'}`}
                >
                  {showLag ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />}
                  <FontAwesomeIcon icon={faArrowTrendDown} className="h-3 w-3 text-red-400" />
                  Lag Indicators ({lagIndicators.length})
                </button>
                {showLag && lagIndicators.map((ind) => (
                  <div
                    key={ind.id}
                    onClick={() => selectIndicator(ind.id)}
                    className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center gap-1.5 cursor-pointer group ${
                      selectedId === ind.id
                        ? (inline ? 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300 border-l-2 border-blue-500 dark:border-blue-400' : 'bg-blue-500/20 text-blue-300 border-l-2 border-blue-400')
                        : `${ind.enabled ? (inline ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-300 dark:hover:bg-slate-800/50 dark:hover:text-white' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white') : (inline ? 'text-gray-400 hover:bg-gray-50 dark:text-slate-600 dark:hover:bg-slate-800/30' : 'text-slate-600 hover:bg-slate-800/30')} border-l-2 border-transparent`
                    }`}
                  >
                    <button
                      onClick={(e) => toggleIndicator(ind.id, !ind.enabled, e)}
                      className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                      title={ind.enabled ? 'Disable' : 'Enable'}
                    >
                      {ind.enabled
                        ? <FontAwesomeIcon icon={faToggleOn} className="h-3.5 w-3.5 text-emerald-400" />
                        : <FontAwesomeIcon icon={faToggleOff} className={`h-3.5 w-3.5 ${inline ? 'text-gray-400 dark:text-slate-500' : 'text-slate-500'}`} />}
                    </button>
                    <span className={`flex-1 truncate ${!ind.enabled ? 'line-through' : ''}`}>{ind.name}</span>
                    {ind.isCustomized && (
                      <span aria-label="Customized"><FontAwesomeIcon icon={faCode} className="h-3 w-3 text-amber-400 flex-shrink-0" /></span>
                    )}
                    {ind.isCustom && (
                      <button
                        onClick={(e) => deleteCustomIndicator(ind.id, e)}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Lead indicators */}
                <button
                  onClick={() => setShowLead(!showLead)}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-wider mt-2 ${inline ? 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800/50' : 'text-slate-400 hover:bg-slate-800/50'}`}
                >
                  {showLead ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />}
                  <FontAwesomeIcon icon={faArrowTrendUp} className="h-3 w-3 text-emerald-400" />
                  Lead Indicators ({leadIndicators.length})
                </button>
                {showLead && leadIndicators.map((ind) => (
                  <div
                    key={ind.id}
                    onClick={() => selectIndicator(ind.id)}
                    className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center gap-1.5 cursor-pointer group ${
                      selectedId === ind.id
                        ? (inline ? 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300 border-l-2 border-blue-500 dark:border-blue-400' : 'bg-blue-500/20 text-blue-300 border-l-2 border-blue-400')
                        : `${ind.enabled ? (inline ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-300 dark:hover:bg-slate-800/50 dark:hover:text-white' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white') : (inline ? 'text-gray-400 hover:bg-gray-50 dark:text-slate-600 dark:hover:bg-slate-800/30' : 'text-slate-600 hover:bg-slate-800/30')} border-l-2 border-transparent`
                    }`}
                  >
                    <button
                      onClick={(e) => toggleIndicator(ind.id, !ind.enabled, e)}
                      className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                      title={ind.enabled ? 'Disable' : 'Enable'}
                    >
                      {ind.enabled
                        ? <FontAwesomeIcon icon={faToggleOn} className="h-3.5 w-3.5 text-emerald-400" />
                        : <FontAwesomeIcon icon={faToggleOff} className={`h-3.5 w-3.5 ${inline ? 'text-gray-400 dark:text-slate-500' : 'text-slate-500'}`} />}
                    </button>
                    <span className={`flex-1 truncate ${!ind.enabled ? 'line-through' : ''}`}>{ind.name}</span>
                    {ind.isCustomized && (
                      <span aria-label="Customized"><FontAwesomeIcon icon={faCode} className="h-3 w-3 text-amber-400 flex-shrink-0" /></span>
                    )}
                    {ind.isCustom && (
                      <button
                        onClick={(e) => deleteCustomIndicator(ind.id, e)}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Create new indicator button */}
                <div className={`px-3 py-3 border-t mt-2 space-y-2 ${inline ? 'border-gray-200 dark:border-slate-700/50' : 'border-slate-700/50'}`}>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-500 dark:bg-blue-600/20 dark:text-blue-400 dark:hover:bg-blue-600/30 dark:hover:text-blue-300 border border-blue-500/50 dark:border-blue-500/30 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
                    Create New Indicator
                  </button>
                  <div className="flex gap-1.5">
                    <button
                      onClick={async () => {
                        try {
                          const { downloadIndicatorExport } = await import('@/services/profile-export');
                          await downloadIndicatorExport();
                        } catch (err) {
                          console.error('Export failed:', err);
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded-lg transition-colors ${inline ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-500/30' : 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'} border ${inline ? 'border-violet-200 dark:border-violet-500/30' : 'border-violet-500/30'}`}
                      title="Export all indicators to JSON"
                    >
                      <FontAwesomeIcon icon={faArrowTrendDown} className="h-3 w-3" />
                      Export
                    </button>
                    <label
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded-lg transition-colors cursor-pointer ${inline ? 'bg-gray-100 dark:bg-slate-700/50 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700' : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'} border ${inline ? 'border-gray-200 dark:border-slate-600' : 'border-slate-600'}`}
                      title="Import indicators from JSON"
                    >
                      <FontAwesomeIcon icon={faArrowTrendUp} className="h-3 w-3" />
                      Import
                      <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const { importIndicators } = await import('@/services/profile-export');
                            const result = await importIndicators(file);
                            alert(`Imported: ${result.imported.join(', ')}. Reload to see changes.`);
                            window.location.reload();
                          } catch (err) {
                            alert(`Import failed: ${(err as Error).message}`);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main editor area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!selectedId || !detail ? (
              <div className={`flex-1 flex items-center justify-center ${inline ? 'text-gray-400 dark:text-slate-500' : 'text-slate-500'}`}>
                <div className="text-center space-y-3">
                  <FontAwesomeIcon icon={faFileCode} className={`h-12 w-12 mx-auto ${inline ? 'text-gray-300 dark:text-slate-600' : 'text-slate-600'}`} />
                  <p className="text-sm">Select an indicator from the sidebar to edit its code</p>
                  <p className={`text-xs max-w-sm mx-auto ${inline ? 'text-gray-400 dark:text-slate-600' : 'text-slate-600'}`}>
                    Each indicator receives <code className="text-blue-400">issues</code>, <code className="text-blue-400">targetDevs</code>, <code className="text-blue-400">devMap</code>, <code className="text-blue-400">helpers</code>, and <code className="text-blue-400">devInfo</code> — and must return a result object.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Editor header */}
                <div className={`flex items-center justify-between px-4 py-3 border-b ${inline ? 'border-gray-200 bg-gray-50/80 dark:border-slate-700/50 dark:bg-slate-800/30' : 'border-slate-700/50 bg-slate-800/30'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${detail.isCustomized ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                    <div>
                      <h3 className={`text-sm font-medium ${inline ? 'text-gray-900 dark:text-white' : 'text-white'}`}>
                        {detail.id}. {detail.name}
                        {hasUnsavedChanges && <span className="text-amber-400 ml-2">*</span>}
                      </h3>
                      <p className={`text-[10px] ${inline ? 'text-gray-500 dark:text-slate-500' : 'text-slate-500'}`}>{detail.description}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <button
                          onClick={async () => {
                            const newScope = (detail.scope || 'per-user') === 'per-user' ? 'overall' : 'per-user';
                            await api.updateIndicatorConfig(detail.id, { scope: newScope });
                            setDetail({ ...detail, scope: newScope });
                            await loadIndicators();
                          }}
                          className={`px-1.5 py-0.5 text-[9px] rounded font-medium cursor-pointer hover:ring-1 hover:ring-white/20 transition-all ${
                            (detail.scope || 'per-user') === 'per-user' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                          }`}
                          title="Click to toggle scope"
                        >{(detail.scope || 'per-user') === 'per-user' ? 'Per User' : 'Overall'}</button>
                        <button
                          onClick={async () => {
                            const current = detail.source || 'jira';
                            const newSource = current === 'jira' ? 'git' : current === 'git' ? 'both' : 'jira';
                            await api.updateIndicatorConfig(detail.id, { source: newSource });
                            setDetail({ ...detail, source: newSource });
                            await loadIndicators();
                          }}
                          className={`px-1.5 py-0.5 text-[9px] rounded font-medium cursor-pointer hover:ring-1 hover:ring-white/20 transition-all ${
                            (detail.source || 'jira') === 'jira' ? 'bg-cyan-500/20 text-cyan-400' : (detail.source || 'jira') === 'git' ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'
                          }`}
                          title="Click to cycle source: Jira → Git → Both"
                        >{(detail.source || 'jira') === 'both' ? 'Git + Jira' : (detail.source || 'jira').toUpperCase()}</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Validate button */}
                    <button
                      onClick={validateCode}
                      disabled={validating}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors disabled:opacity-50 ${inline ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'}`}
                    >
                      {validating ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faPlay} className="h-3 w-3" />}
                      Validate
                    </button>
                    {/* Toggle enable/disable */}
                    <button
                      onClick={(e) => toggleIndicator(detail.id, !detail.enabled, e)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                        detail.enabled
                          ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {detail.enabled ? <FontAwesomeIcon icon={faToggleOn} className="h-3 w-3" /> : <FontAwesomeIcon icon={faToggleOff} className="h-3 w-3" />}
                      {detail.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    {/* Reset to default */}
                    {detail.isCustomized && !detail.isCustom && (
                      <button
                        onClick={resetToDefault}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${inline ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'}`}
                      >
                        <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />
                        Reset
                      </button>
                    )}
                    {/* Delete custom indicator */}
                    {detail.isCustom && (
                      <button
                        onClick={(e) => deleteCustomIndicator(detail.id, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                        Delete
                      </button>
                    )}
                    {/* Save button */}
                    <button
                      onClick={saveCode}
                      disabled={saving || !hasUnsavedChanges}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faFloppyDisk} className="h-3 w-3" />}
                      Save (⌘S)
                    </button>
                  </div>
                </div>

                {/* Validation / save feedback */}
                {(validationResult || saveResult) && (
                  <div className={`px-4 py-2 border-b ${inline ? 'border-gray-200 dark:border-slate-700/50' : 'border-slate-700/50'}`}>
                    {validationResult && validationResult.securityWarnings && validationResult.securityWarnings.length > 0 ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-red-400 font-medium">
                          <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5" />
                          Security: blocked {validationResult.securityWarnings.length} dangerous pattern{validationResult.securityWarnings.length > 1 ? 's' : ''}
                        </div>
                        {validationResult.securityWarnings.map((w, i) => (
                          <div key={i} className="text-[10px] text-red-400/80 pl-5">- {w}</div>
                        ))}
                      </div>
                    ) : validationResult ? (
                      <div className={`flex items-center gap-2 text-xs ${validationResult.valid ? 'text-emerald-400' : 'text-red-400'}`}>
                        {validationResult.valid ? <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> : <FontAwesomeIcon icon={faCircleExclamation} className="h-3 w-3" />}
                        {validationResult.valid ? 'Code is valid and passes security checks' : `Error: ${validationResult.error}`}
                      </div>
                    ) : null}
                    {saveResult && (
                      <div className={`flex items-center gap-2 text-xs ${saveResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {saveResult.success ? <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> : <FontAwesomeIcon icon={faCircleExclamation} className="h-3 w-3" />}
                        {saveResult.success ? 'Saved successfully' : `Save failed: ${saveResult.error}`}
                      </div>
                    )}
                  </div>
                )}

                {/* Context info banner */}
                <div className={`px-4 py-2 border-b ${inline ? 'border-gray-200 bg-gray-50/50 dark:border-slate-700/30 dark:bg-slate-800/20' : 'border-slate-700/30 bg-slate-800/20'}`}>
                  <p className={`text-[10px] font-mono ${inline ? 'text-gray-500 dark:text-slate-500' : 'text-slate-500'}`}>
                    <span className={inline ? 'text-blue-600 dark:text-blue-400' : 'text-blue-400'}>Available:</span>{' '}
                    <span className={inline ? 'text-purple-600 dark:text-purple-300' : 'text-purple-300'}>issues</span> (JiraIssue[]),{' '}
                    <span className={inline ? 'text-purple-600 dark:text-purple-300' : 'text-purple-300'}>targetDevs</span> (string[]),{' '}
                    <span className={inline ? 'text-purple-600 dark:text-purple-300' : 'text-purple-300'}>devMap</span> (Map&lt;name, accountId&gt;),{' '}
                    <span className={inline ? 'text-purple-600 dark:text-purple-300' : 'text-purple-300'}>helpers</span> (.isTestingStatus, .isDoneStatus, .isInProgressStatus, .isDevReworkStatus, .isReleaseCandidateStatus, .isProductionHotfix, .statusMatches, .STATUS),{' '}
                    <span className={inline ? 'text-purple-600 dark:text-purple-300' : 'text-purple-300'}>devInfo</span> (Map&lt;issueId, &#123;commits, pullRequests&#125;&gt;)
                    {detail.needsDevInfo && <span className="text-amber-400 ml-2">⚡ This indicator uses devInfo</span>}
                  </p>
                </div>

                {/* Monaco editor */}
                <div className="flex-1 overflow-hidden">
                  <Editor
                    key={`indicator-${selectedId}`}
                    height="100%"
                    language="typescript"
                    path={`indicator-${selectedId}.ts`}
                    theme={inline && !isDark ? 'verigit-light' : 'verigit-dark'}
                    value={code}
                    beforeMount={(monaco) => {
                      // Override Monarch tokenizer for rich coloring
                      monaco.languages.setMonarchTokensProvider('typescript', {
                        keywords: [
                          'break', 'case', 'catch', 'class', 'continue', 'const',
                          'debugger', 'default', 'delete', 'do', 'else', 'export',
                          'extends', 'finally', 'for', 'from', 'function', 'get',
                          'if', 'import', 'in', 'instanceof', 'let', 'new',
                          'of', 'return', 'set', 'super', 'switch', 'this',
                          'throw', 'try', 'typeof', 'var', 'void', 'while',
                          'with', 'yield', 'async', 'await', 'static',
                          'declare', 'interface', 'type', 'enum', 'implements',
                          'private', 'protected', 'public', 'abstract', 'as',
                        ],
                        typeKeywords: [
                          'any', 'boolean', 'number', 'object', 'string', 'undefined',
                          'never', 'void', 'null', 'symbol', 'bigint',
                        ],
                        constants: ['true', 'false', 'null', 'undefined', 'NaN', 'Infinity'],
                        builtins: [
                          'Array', 'Map', 'Set', 'Date', 'Math', 'JSON', 'Object',
                          'String', 'Number', 'Boolean', 'RegExp', 'Error', 'Promise',
                          'console', 'parseInt', 'parseFloat', 'isNaN', 'isFinite',
                        ],
                        globals: [
                          'issues', 'targetDevs', 'devMap', 'helpers', 'devInfo',
                        ],
                        operators: [
                          '<=', '>=', '==', '!=', '===', '!==', '=>', '+', '-', '**',
                          '*', '/', '%', '++', '--', '<<', '>>', '>>>', '&', '|', '^',
                          '!', '~', '&&', '||', '??', '?', ':', '=', '+=', '-=', '*=',
                          '/=', '%=', '<<=', '>>=', '>>>=', '&=', '|=', '^=', '...', '?.',
                        ],
                        symbols: /[=><!~?:&|+\-*\/\^%]+/,
                        escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
                        tokenizer: {
                          root: [
                            [/\/\/.*$/, 'comment'],
                            [/\/\*/, 'comment', '@comment'],
                            [/"([^"\\]|\\.)*$/, 'string.invalid'],
                            [/'([^'\\]|\\.)*$/, 'string.invalid'],
                            [/"/, 'string', '@string_double'],
                            [/'/, 'string', '@string_single'],
                            [/`/, 'string.template', '@string_backtick'],
                            [/0[xX][0-9a-fA-F]+/, 'number.hex'],
                            [/0[oO][0-7]+/, 'number.octal'],
                            [/0[bB][01]+/, 'number.binary'],
                            [/\d+\.?\d*([eE][-+]?\d+)?/, 'number'],
                            [/\.\d+([eE][-+]?\d+)?/, 'number'],
                            [/(\.)([a-zA-Z_$][\w$]*)(\s*\()/, ['delimiter', 'method', 'delimiter']],
                            [/(\.)([a-zA-Z_$][\w$]*)/, ['delimiter', 'property']],
                            [/(\?\.)([a-zA-Z_$][\w$]*)(\s*\()/, ['delimiter', 'method', 'delimiter']],
                            [/(\?\.)([a-zA-Z_$][\w$]*)/, ['delimiter', 'property']],
                            [/([a-zA-Z_$][\w$]*)(\s*\()/, {
                              cases: {
                                '$1@keywords': ['keyword', 'delimiter'],
                                '$1@builtins': ['builtin', 'delimiter'],
                                '$1@globals': ['global', 'delimiter'],
                                '@default': ['function-call', 'delimiter'],
                              }
                            }],
                            [/[a-zA-Z_$][\w$]*/, {
                              cases: {
                                '@keywords': 'keyword',
                                '@typeKeywords': 'type',
                                '@constants': 'constant',
                                '@builtins': 'builtin',
                                '@globals': 'global',
                                '@default': 'variable',
                              }
                            }],
                            [/[{}()\[\]]/, 'bracket'],
                            [/[;,]/, 'delimiter'],
                            [/@symbols/, {
                              cases: { '@operators': 'operator', '@default': '' }
                            }],
                          ],
                          comment: [
                            [/[^\/*]+/, 'comment'],
                            [/\*\//, 'comment', '@pop'],
                            [/[\/*]/, 'comment'],
                          ],
                          string_double: [
                            [/[^\\"]+/, 'string'],
                            [/@escapes/, 'string.escape'],
                            [/\\./, 'string.escape.invalid'],
                            [/"/, 'string', '@pop'],
                          ],
                          string_single: [
                            [/[^\\']+/, 'string'],
                            [/@escapes/, 'string.escape'],
                            [/\\./, 'string.escape.invalid'],
                            [/'/, 'string', '@pop'],
                          ],
                          string_backtick: [
                            [/\$\{/, { token: 'string.template', next: '@template_expr' }],
                            [/[^\\`$]+/, 'string.template'],
                            [/@escapes/, 'string.escape'],
                            [/\\./, 'string.escape.invalid'],
                            [/`/, 'string.template', '@pop'],
                          ],
                          template_expr: [
                            [/\}/, { token: 'string.template', next: '@pop' }],
                            { include: 'root' },
                          ],
                        },
                      });

                      // Force TS worker to load eagerly despite tokenizer override
                      monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true);

                      // Configure TypeScript compiler & diagnostics
                      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
                        target: monaco.languages.typescript.ScriptTarget.ESNext,
                        allowNonTsExtensions: true,
                        allowJs: true,
                        checkJs: false,
                        noEmit: true,
                        strict: false,
                      });
                      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                        noSemanticValidation: false,
                        noSyntaxValidation: false,
                        // Suppress "A 'return' statement can only be used within a function body"
                        // Our code runs inside new Function() which provides the function wrapper
                        diagnosticCodesToIgnore: [1108],
                      });

                      // Type definitions for autocomplete & diagnostics
                      const typeDefs = `
declare interface JiraIssueFields {
  summary: string;
  status: { name: string; id: string };
  issuetype: { name: string; id: string };
  assignee: { displayName: string; accountId: string; avatarUrls?: any } | null;
  reporter: { displayName: string; accountId: string } | null;
  priority: { name: string; id: string } | null;
  created: string;
  updated: string;
  resolutiondate: string | null;
  components: { name: string; id: string }[];
  labels: string[];
  sprint: { id: number; name: string; startDate?: string; endDate?: string; state?: string } | null;
  parent: { key: string; fields: { summary: string } } | null;
  timeoriginalestimate: number | null;
  timespent: number | null;
  story_points?: number;
  customfield_10016?: number;
  [key: string]: any;
}
declare interface JiraChangelog {
  histories: Array<{
    id: string;
    created: string;
    author: { displayName: string; accountId: string } | null;
    items: Array<{
      field: string;
      fieldtype: string;
      fromString: string | null;
      toString: string | null;
      from: string | null;
      to: string | null;
    }>;
  }>;
}
declare interface JiraIssue {
  id: string;
  key: string;
  fields: JiraIssueFields;
  changelog?: JiraChangelog;
}
declare interface DevResult {
  developerName: string;
  developerAccountId: string;
  value: number;
  label: string;
  severity: "good" | "warning" | "critical";
  details: string;
  issueKeys?: string[];
}
declare interface IndicatorResult {
  id: number;
  name: string;
  description: string;
  results: DevResult[];
  overallValue: number;
  overallLabel: string;
  severity: "good" | "warning" | "critical";
}
declare const helpers: {
  STATUS: {
    TODO: readonly string[];
    IN_PROGRESS: readonly string[];
    RELEASE_CANDIDATE: readonly string[];
    TESTING: readonly string[];
    READY: readonly string[];
    DEV_REWORK: readonly string[];
    DONE: readonly string[];
  };
  statusMatches(status: string, group: readonly string[]): boolean;
  isTestingStatus(status: string): boolean;
  isDevReworkStatus(status: string): boolean;
  isReleaseCandidateStatus(status: string): boolean;
  isDoneStatus(status: string): boolean;
  isInProgressStatus(status: string): boolean;
  isProductionHotfix(status: string): boolean;
};
declare const issues: JiraIssue[];
declare const targetDevs: string[];
declare const devMap: Map<string, string>;
declare const devInfo: Map<string, { commits: number; pullRequests: number }>;
`;
                      monaco.languages.typescript.typescriptDefaults.addExtraLib(typeDefs, 'indicator-context.d.ts');

                      // Define light theme (GitHub-inspired)
                      monaco.editor.defineTheme('verigit-light', {
                        base: 'vs',
                        inherit: true,
                        rules: [
                          { token: 'keyword', foreground: 'CF222E', fontStyle: 'bold' },
                          { token: 'type', foreground: '0550AE' },
                          { token: 'variable', foreground: '953800' },
                          { token: 'global', foreground: '953800', fontStyle: 'bold' },
                          { token: 'property', foreground: '953800' },
                          { token: 'method', foreground: '8250DF' },
                          { token: 'function-call', foreground: '8250DF' },
                          { token: 'builtin', foreground: '0550AE' },
                          { token: 'constant', foreground: '0550AE' },
                          { token: 'string', foreground: '0A3069' },
                          { token: 'string.invalid', foreground: '0A3069' },
                          { token: 'string.escape', foreground: '0550AE' },
                          { token: 'string.template', foreground: '0A3069' },
                          { token: 'number', foreground: '0550AE' },
                          { token: 'number.hex', foreground: '0550AE' },
                          { token: 'comment', foreground: '6E7781', fontStyle: 'italic' },
                          { token: 'operator', foreground: 'CF222E' },
                          { token: 'bracket', foreground: '0550AE' },
                          { token: 'delimiter', foreground: '24292F' },
                          { token: '', foreground: '24292F' },
                        ],
                        colors: {
                          'editor.background': '#FFFFFF',
                          'editor.foreground': '#24292F',
                          'editor.lineHighlightBackground': '#F6F8FA',
                          'editor.selectionBackground': '#ADD6FF80',
                          'editorCursor.foreground': '#0550AE',
                          'editorWhitespace.foreground': '#D1D5DB',
                          'editorIndentGuide.background': '#D1D5DB',
                          'editorIndentGuide.activeBackground': '#9CA3AF',
                          'editorLineNumber.foreground': '#9CA3AF',
                          'editorLineNumber.activeForeground': '#24292F',
                          'editorBracketMatch.background': '#ADD6FF40',
                          'editorBracketMatch.border': '#0550AE',
                          'scrollbarSlider.background': '#D1D5DB80',
                          'editorWidget.background': '#FFFFFF',
                          'editorSuggestWidget.background': '#FFFFFF',
                          'editorSuggestWidget.border': '#E5E7EB',
                          'editorSuggestWidget.selectedBackground': '#F3F4F6',
                          'editorHoverWidget.background': '#FFFFFF',
                          'editorHoverWidget.border': '#E5E7EB',
                        },
                      });

                      // Define vivid theme (One Dark Pro inspired)
                      monaco.editor.defineTheme('verigit-dark', {
                        base: 'vs-dark',
                        inherit: true,
                        rules: [
                          { token: 'keyword', foreground: 'C678DD', fontStyle: 'bold' },
                          { token: 'type', foreground: '56B6C2' },
                          { token: 'variable', foreground: 'E06C75' },
                          { token: 'global', foreground: 'E06C75', fontStyle: 'bold' },
                          { token: 'property', foreground: 'E06C75' },
                          { token: 'method', foreground: '61AFEF' },
                          { token: 'function-call', foreground: '61AFEF' },
                          { token: 'builtin', foreground: 'E5C07B' },
                          { token: 'constant', foreground: 'D19A66' },
                          { token: 'string', foreground: '98C379' },
                          { token: 'string.invalid', foreground: '98C379' },
                          { token: 'string.escape', foreground: '56B6C2' },
                          { token: 'string.template', foreground: '98C379' },
                          { token: 'number', foreground: 'D19A66' },
                          { token: 'number.hex', foreground: 'D19A66' },
                          { token: 'comment', foreground: '5C6370', fontStyle: 'italic' },
                          { token: 'operator', foreground: '56B6C2' },
                          { token: 'bracket', foreground: 'E5C07B' },
                          { token: 'delimiter', foreground: 'ABB2BF' },
                          { token: '', foreground: 'ABB2BF' },
                        ],
                        colors: {
                          'editor.background': '#0f172a',
                          'editor.foreground': '#ABB2BF',
                          'editor.lineHighlightBackground': '#1e293b',
                          'editor.selectionBackground': '#3E4451',
                          'editorCursor.foreground': '#528BFF',
                          'editorWhitespace.foreground': '#3B4048',
                          'editorIndentGuide.background': '#3B4048',
                          'editorIndentGuide.activeBackground': '#636D83',
                          'editorLineNumber.foreground': '#495162',
                          'editorLineNumber.activeForeground': '#ABB2BF',
                          'editorBracketMatch.background': '#3E4451',
                          'editorBracketMatch.border': '#528BFF',
                          'scrollbarSlider.background': '#4E566680',
                          'editorWidget.background': '#1e293b',
                          'editorSuggestWidget.background': '#1e293b',
                          'editorSuggestWidget.border': '#334155',
                          'editorSuggestWidget.selectedBackground': '#334155',
                          'editorHoverWidget.background': '#1e293b',
                          'editorHoverWidget.border': '#334155',
                        },
                      });
                    }}
                    onChange={(value) => {
                      setCode(value || '');
                      setHasUnsavedChanges(true);
                      setValidationResult(null);
                      setSaveResult(null);
                    }}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      tabSize: 2,
                      automaticLayout: true,
                      padding: { top: 12 },
                      bracketPairColorization: { enabled: true },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
  );

  return (<>
    {inline ? editorContent : createPortal(
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
        {editorContent}
      </div>,
      document.body
    )}
    {showCreateModal && createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600/50 rounded-xl w-full max-w-md p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Indicator</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Name *</label>
            <input
              type="text"
              value={newIndicator.name}
              onChange={(e) => setNewIndicator({ ...newIndicator, name: e.target.value })}
              placeholder="e.g. Sprint Velocity"
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Description</label>
            <input
              type="text"
              value={newIndicator.description}
              onChange={(e) => setNewIndicator({ ...newIndicator, description: e.target.value })}
              placeholder="What does this indicator measure?"
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Type *</label>
            <div className="flex gap-2">
              <button
                onClick={() => setNewIndicator({ ...newIndicator, type: 'lag' })}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                  newIndicator.type === 'lag'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-gray-400 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faArrowTrendDown} className="h-3.5 w-3.5" />
                Lag Indicator
              </button>
              <button
                onClick={() => setNewIndicator({ ...newIndicator, type: 'lead' })}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                  newIndicator.type === 'lead'
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-gray-400 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5" />
                Lead Indicator
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Scope *</label>
            <div className="flex gap-2">
              <button
                onClick={() => setNewIndicator({ ...newIndicator, scope: 'per-user' })}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                  newIndicator.scope === 'per-user'
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-gray-400 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" />
                Per User
              </button>
              <button
                onClick={() => setNewIndicator({ ...newIndicator, scope: 'overall' })}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                  newIndicator.scope === 'overall'
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-gray-400 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5" />
                Overall
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Data Source *</label>
            <div className="grid grid-cols-3 gap-1.5">
              {([
                { key: 'jira', label: 'Jira', active: 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' },
                { key: 'linear', label: 'Linear', active: 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400' },
                { key: 'monday', label: 'Monday', active: 'bg-pink-500/20 border-pink-500/50 text-pink-400' },
                { key: 'trello', label: 'Trello', active: 'bg-sky-500/20 border-sky-500/50 text-sky-400' },
                { key: 'git', label: 'Git', active: 'bg-orange-500/20 border-orange-500/50 text-orange-400' },
                { key: 'both', label: 'Both', active: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' },
              ] as const).map(({ key, label, active }) => (
                <button
                  key={key}
                  onClick={() => setNewIndicator({ ...newIndicator, source: key })}
                  className={`flex items-center justify-center px-2 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    newIndicator.source === key
                      ? active
                      : 'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-gray-400 dark:hover:border-slate-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="needsDevInfo"
              checked={newIndicator.needsDevInfo}
              onChange={(e) => setNewIndicator({ ...newIndicator, needsDevInfo: e.target.checked })}
              className="rounded border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 text-blue-500 focus:ring-blue-500"
            />
            <label htmlFor="needsDevInfo" className="text-xs text-gray-500 dark:text-slate-400">
              Needs dev info (commit/PR data)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => setShowCreateModal(false)}
            className="px-4 py-2 text-xs font-medium rounded-lg text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={createNewIndicator}
            disabled={!newIndicator.name.trim()}
            className="px-4 py-2 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Create
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}
  </>);
}
